import React, { useMemo, useState } from 'react'
import {
  StopOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import { Button, Space, Tag, Modal, Input, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import TableIndex from '@shared/table/TableIndex'
import type { TableService } from '@shared/table/TableIndex'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import { formatDateTime } from '@utils/dateTime'
import OrderStatusLabel from '@shared/orders/OrderStatusLabel'
import ReleaseHoldModal from '@shared/orders/ReleaseHoldModal'

interface MerchantOption {
  id: string;
  name: string;
}

interface OrderHoldService extends TableService {
  batchRelease: (ids: string[], note: string) => Promise<{ success: boolean; message?: string }>;
}

interface OrderHoldListProps {
  service: OrderHoldService;
  role?: 'operator' | 'admin';
  merchants?: MerchantOption[];
}

interface OrderHoldRow {
  id: string;
  order: { id: string; name: string; status?: string };
  merchant: { id: string; name: string };
  reason: string;
  note?: string;
  held_by?: string;
  created_at?: string;
  [key: string]: unknown;
}

const OrderHoldList: React.FC<OrderHoldListProps> = ({ service, role = 'operator', merchants = [] }) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)
  const [batchReleaseVisible, setBatchReleaseModalVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [releasing, setReleasing] = useState(false)
  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo(() => [
    {
      nameKey: 'models.order.order_number',
      persist: true,
      cell: ({ order }: OrderHoldRow) => (
        <Space>
          <Link to={`/orders/${order.id}`}>{order.name}</Link>
          <OrderStatusLabel status={order.status} />
        </Space>
      )
    },
    {
      nameKey: 'models.merchant.name',
      selector: ({ merchant }: OrderHoldRow) => merchant.name,
    },
    {
      nameKey: 'models.order_hold.reason',
      cell: ({ reason }: OrderHoldRow) => (
        <Tag color="error">
          {t(`enums.order_hold.reason.${reason}`, { defaultValue: reason })}
        </Tag>
      )
    },
    {
      nameKey: 'models.order_hold.note',
      selector: ({ note }: OrderHoldRow) => note,
      wrap: true
    },
    {
      nameKey: 'models.order_hold.held_by',
      cell: ({ held_by }: OrderHoldRow) => held_by || (
        <Tag>{t('models.order_hold.held_by_system')}</Tag>
      ),
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'order_holds.created_at',
      cell: ({ created_at }: OrderHoldRow) => formatDateTime(created_at as string),
    }
  ], [t, urlPrefix])

  const supportRules = useMemo(() => [
    ...(merchants.length > 0 ? [{
      label: t('shared.orders.list.filters.merchant'),
      component: MultipleValue,
      inputName: 'merchant_id',
      defaultOptions: merchants.map(m => ({ label: m.name, value: m.id }))
    }] : []),
    {
      label: t('models.order_hold.reason'),
      component: MultipleValue,
      inputName: 'reason',
      defaultOptions: [
        'merchant_request', 'platform_cancel', 'risk_control',
        'address_issue', 'convenience_store_issue', 'inventory_discrepancy', 'other'
      ].map(r => ({ label: t(`enums.order_hold.reason.${r}`, { defaultValue: r }), value: r }))
    },
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'created_at'
    }
  ], [t, merchants])

  const handleBatchRelease = (selected: Set<string>) => {
    setSelectedIds(selected)
    setBatchReleaseModalVisible(true)
  }

  const confirmBatchRelease = async (note: string) => {
    try {
      setReleasing(true)
      const res = await service.batchRelease(Array.from(selectedIds), note)
      if (res.success) {
        message.success(t('shared.order_holds.list.release_success'))
        setBatchReleaseModalVisible(false)
        setLastModified(Date.now())
      } else {
        message.error(res.message || t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to batch release order holds:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setReleasing(false)
    }
  }

  const actions = useMemo(() => [
    {
      name: 'shared.orders.detail.actions.release_holds',
      icon: <CheckCircleOutlined />,
      type: 'primary',
      do: handleBatchRelease
    }
  ], [t, service])

  return (
    <>
      <TableIndex
        tableId="OrderHoldsTable"
        columns={columns}
        supportRules={supportRules}
        actions={actions}
        service={service}
        lastModified={lastModified}
        setLastModified={setLastModified}
        defaultQuery={{
          sorting_field: 'order_holds.id',
          sorting_direction: 'desc'
        }}
      />

      <ReleaseHoldModal
        visible={batchReleaseVisible}
        onCancel={() => setBatchReleaseModalVisible(false)}
        onConfirm={confirmBatchRelease}
        count={selectedIds.size}
        confirmLoading={releasing}
      />
    </>
  )
}

export default OrderHoldList
